<template>
  <v-dialog v-model="show" max-width="960px" dark persistent scrollable>
    <v-card dark>
      <v-card-title class="text-h6">
        Import Products
        <v-spacer />
        <v-btn icon :disabled="busy" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />

      <v-card-text class="pa-4">
        <p class="mb-3">
          Import standard products for
          <strong>{{ eventName }}</strong
          >. Download the template, fill it in, then upload to preview what will
          be created.
        </p>

        <div class="d-flex flex-wrap mb-4">
          <v-btn
            small
            dark
            class="mr-2 mb-2"
            :loading="downloading"
            :disabled="busy"
            @click="downloadTemplate"
          >
            <v-icon left x-small>mdi-download</v-icon>
            Download Template
          </v-btn>
          <v-btn
            small
            dark
            class="mb-2"
            :loading="validating"
            :disabled="busy"
            @click="$refs.fileInput.click()"
          >
            <v-icon left x-small>mdi-upload</v-icon>
            Upload CSV
          </v-btn>
          <input
            ref="fileInput"
            type="file"
            accept=".csv"
            class="d-none"
            @change="handleFileUpload"
          />
        </div>

        <v-alert v-if="csvError" type="error" dense outlined class="mb-4">
          {{ csvError }}
        </v-alert>

        <template v-if="preview">
          <v-alert
            v-if="preview.Errors && preview.Errors.length"
            type="warning"
            dense
            outlined
            class="mb-4"
          >
            {{ preview.Errors.length }} row{{
              preview.Errors.length === 1 ? "" : "s"
            }}
            will be skipped.
          </v-alert>

          <div class="preview-section mb-6">
            <div class="text-subtitle-1 font-weight-medium mb-1">
              New categories ({{ categoryRows.length }})
            </div>
            <div class="caption grey--text mb-2">
              Created once per stallholder if that category name does not already
              exist.
            </div>
            <v-simple-table v-if="categoryRows.length" dense dark>
              <thead>
                <tr>
                  <th>Stallholder</th>
                  <th>Category</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in categoryRows" :key="'cat-' + idx">
                  <td>{{ row.StallholderName }}</td>
                  <td>{{ row.CategoryName }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <div v-else class="caption grey--text">
              No new categories. Products will use categories that already exist.
            </div>
          </div>

          <div class="preview-section mb-6">
            <div class="text-subtitle-1 font-weight-medium mb-1">
              New event tags ({{ tagRows.length }})
            </div>
            <div class="caption grey--text mb-2">
              Shared across the event. Existing tag names are reused.
            </div>
            <v-simple-table v-if="tagRows.length" dense dark>
              <thead>
                <tr>
                  <th>Tag</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in tagRows" :key="'tag-' + idx">
                  <td>{{ row.ProductTagName }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <div v-else class="caption grey--text">
              No new tags. Existing event tags will be reused where needed.
            </div>
          </div>

          <div class="preview-section mb-6">
            <div class="text-subtitle-1 font-weight-medium mb-1">
              New products ({{ productRows.length }})
            </div>
            <div class="caption grey--text mb-2">
              These products will be created on import.
            </div>
            <v-simple-table v-if="productRows.length" dense dark>
              <thead>
                <tr>
                  <th>Stallholder</th>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Tag</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in productRows" :key="'prod-' + idx">
                  <td>{{ row.StallholderName }}</td>
                  <td>{{ row.ProductName }}</td>
                  <td>{{ row.CategoryName }}</td>
                  <td>{{ row.TagName || "—" }}</td>
                </tr>
              </tbody>
            </v-simple-table>
            <div v-else class="caption grey--text">
              No products to create. Fix the errors below and upload again.
            </div>
          </div>

          <div v-if="preview.Errors && preview.Errors.length" class="preview-section">
            <div class="text-subtitle-1 font-weight-medium mb-1">
              Rows that will be skipped ({{ preview.Errors.length }})
            </div>
            <div class="caption grey--text mb-2">
              These rows will not be imported.
            </div>
            <v-simple-table dense dark>
              <thead>
                <tr>
                  <th>CSV row</th>
                  <th>Product</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(err, idx) in preview.Errors" :key="'err-' + idx">
                  <td>{{ err.Row }}</td>
                  <td>{{ err.ProductName || "—" }}</td>
                  <td>{{ err.Message }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn text :disabled="busy" @click="close">Cancel</v-btn>
        <v-btn
          color="primary"
          :loading="importing"
          :disabled="!canImport"
          @click="runImport"
        >
          Import
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const TEMPLATE_HEADERS = [
  "Stallholder",
  "Category",
  "Tag",
  "Product Name",
  "Short Name",
  "Price",
  "Drink Limit Value",
  "GST Applicable",
];

export default {
  name: "ProductImportDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    eventId: {
      type: String,
      default: "",
    },
    eventName: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      csvError: "",
      rows: [],
      preview: null,
      validating: false,
      importing: false,
      downloading: false,
    };
  },

  computed: {
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    busy() {
      return this.validating || this.importing || this.downloading;
    },

    canImport() {
      return (
        !this.busy &&
        this.preview &&
        (this.preview.ProductsToCreate || []).length > 0
      );
    },

    categoryRows() {
      return (this.preview && this.preview.CategoriesToCreate) || [];
    },

    tagRows() {
      return (this.preview && this.preview.TagsToCreate) || [];
    },

    productRows() {
      return (this.preview && this.preview.ProductsToCreate) || [];
    },
  },

  watch: {
    value(open) {
      if (open) this.reset();
    },
  },

  methods: {
    reset() {
      this.csvError = "";
      this.rows = [];
      this.preview = null;
      this.validating = false;
      this.importing = false;
      this.downloading = false;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    close() {
      if (this.busy) return;
      this.show = false;
    },

    downloadTemplate() {
      if (this.downloading) return;
      this.downloading = true;
      this.$nextTick(() => {
        try {
          const example = [
            "Example Stallholder",
            "Beer",
            "Happy Hour",
            "Lager",
            "Lager",
            "8.50",
            "1.5",
            "Yes",
          ];
          const csv = [TEMPLATE_HEADERS.join(","), example.join(",")].join(
            "\r\n"
          );
          const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = "product-import-template.csv";
          link.click();
          URL.revokeObjectURL(url);
        } finally {
          window.setTimeout(() => {
            this.downloading = false;
          }, 400);
        }
      });
    },

    parseCsvLine(line) {
      const values = [];
      let current = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i += 1) {
        const char = line[i];
        if (char === '"') {
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i += 1;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === "," && !inQuotes) {
          values.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      return values;
    },

    parseCsv(content) {
      const lines = content
        .replace(/^\uFEFF/, "")
        .split(/\r?\n/)
        .filter((line) => line.trim() !== "");
      if (!lines.length) throw new Error("CSV is empty");
      const headers = this.parseCsvLine(lines[0]).map((h) => h.trim());
      const missing = TEMPLATE_HEADERS.filter(
        (header) =>
          !headers.some((h) => h.toLowerCase() === header.toLowerCase())
      );
      if (missing.length) {
        throw new Error("Missing columns: " + missing.join(", "));
      }
      const headerIndex = {};
      headers.forEach((header, idx) => {
        headerIndex[header.toLowerCase()] = idx;
      });
      return lines.slice(1).map((line) => {
        const values = this.parseCsvLine(line);
        const row = {};
        TEMPLATE_HEADERS.forEach((header) => {
          row[header] = values[headerIndex[header.toLowerCase()]] || "";
        });
        return row;
      });
    },

    handleFileUpload(event) {
      const file = event.target.files && event.target.files[0];
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
      if (!file) return;
      this.validating = true;
      this.csvError = "";
      this.preview = null;
      if (file.size > 2 * 1024 * 1024) {
        this.csvError = "CSV must be smaller than 2 MB";
        this.validating = false;
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        try {
          this.rows = this.parseCsv(String(reader.result || ""));
          this.validateRows();
        } catch (err) {
          this.csvError = err.message || "Unable to parse CSV";
          this.preview = null;
          this.validating = false;
        }
      };
      reader.onerror = () => {
        this.csvError = "Unable to read the CSV file";
        this.validating = false;
      };
      reader.readAsText(file);
    },

    validateRows() {
      if (!this.eventId) {
        this.csvError = "Select an event before importing";
        this.validating = false;
        return;
      }
      this.$emit("validate", {
        event_items: [
          {
            Type: "product_import",
            Action: "Validate",
            Properties: {
              EventId: this.eventId,
              Rows: this.rows,
            },
          },
        ],
      });
    },

    setPreview(preview) {
      this.validating = false;
      this.preview = preview || null;
    },

    setError(message) {
      this.validating = false;
      this.importing = false;
      this.csvError = message || "Import failed";
    },

    runImport() {
      if (!this.canImport) return;
      this.importing = true;
      this.$emit("import", {
        event_items: [
          {
            Type: "product_import",
            Action: "Import",
            Properties: {
              EventId: this.eventId,
              Rows: this.rows,
            },
          },
        ],
      });
    },
  },
};
</script>
